import { FC, useMemo } from 'react';

import { SxProps, TableCell, TableRow, Theme } from '@mui/material';
import { useFilterStore } from 'app/store';

import { useGetApplications } from 'features/applications';
import { useGetTransactions } from 'features/transactions';

import { CompareIcon, MailIcon } from 'shared/assets/icons';
import { ROLES } from 'shared/constants';
import { useGetDate, useUser } from 'shared/hooks';
import { useUrlParams } from 'shared/hooks/useUrlParams';
import { TApplications } from 'shared/services/types/application.types';
import { TFiltersMethod, TStatusType } from 'shared/services/types/common.types';
import { TTransaction } from 'shared/services/types/transaction.types';
import { MethodTableLayout } from 'shared/ui/method-table-layout';
import { RoleAccessWrapper } from 'shared/ui/role-access-wrapper';
import { Status } from 'shared/ui/status';
import { TableContentWrapper } from 'shared/ui/table-content-wrapper';
import { CustomTooltip } from 'shared/ui/tooltip';
import { getTransformedDate } from 'shared/utils';

import { APPLICATIONS } from '../default-columns/APPLICATIONS';
import { TRANSACTIONS } from '../default-columns/TRANSACTIONS';

interface IProps {
  matchTransaction: (id: number) => void;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  methodID: number;
  onSubmitFilters: (data: TFiltersMethod) => void;
  openMessagesModal: (message: string, time?: string) => void;
}

const customStyles = {
  width: '50%',
  '.MuiTableHead-root': {
    '.MuiTableRow-root': {
      '.MuiTableCell-root': {
        '> svg': {
          marginLeft: '16px !important'
        }
      }
    }
  }
} as SxProps<Theme>;

export const DefaultTables: FC<IProps> = ({
  matchTransaction,
  pageSize,
  methodID,
  setPageSize,
  onSubmitFilters,
  openMessagesModal
}) => {
  const { filter } = useFilterStore();
  const { getDate } = useGetDate();
  const { user } = useUser();

  const { paramsObj } = useUrlParams();
  const page = paramsObj.page ? parseInt(paramsObj.page) - 1 : 0;

  const filterT = useMemo(() => {
    return {
      size: pageSize,
      page: page,
      order_by: 'id',
      method_id: methodID,
      transaction_status: filter.status,
      subagent_id: filter.subagent_id,
      account_id: filter.account_id,
      remarks: filter.remarks,
      date_from: filter.date_from,
      date_to: filter.date_to,
      is_desc_sort: true
    } as TTransaction.GetTransaction;
  }, [onSubmitFilters, page, pageSize]);

  const filterA = useMemo(() => {
    return {
      size: pageSize,
      page: page,
      order_by: 'id',
      method_id: methodID,
      transaction_status: filter.status,
      subagent_id: filter.subagent_id,
      account_id: filter.account_id,
      remarks: filter.remarks,
      date_from: filter.date_from,
      date_to: filter.date_to,
      is_desc_sort: true
    } as TApplications.GetApplication;
  }, [onSubmitFilters, page, pageSize]);

  const { isTransactionsLoading, transactions } = useGetTransactions(filterT);

  const { isApplicationsLoading, applications } = useGetApplications(filterA);

  const total = useMemo(() => {
    if (applications && transactions)
      return applications.total > transactions.total ? applications.total : transactions.total;
  }, [applications, transactions]);

  return (
    <MethodTableLayout
      pageSize={pageSize}
      setPageSize={setPageSize}
      total={total || 0}
      isDataLoading={isApplicationsLoading}>
      <TableContentWrapper columns={APPLICATIONS} sx={customStyles}>
        {applications?.items.map((row, id) => {
          const accountId = methodID === 1 ? row.subagent_id : row.account_id;
          const status = row?.status as TStatusType;
          const isStatusMatch = ['received', 'scam'].includes(row.status);

          return (
            <TableRow key={id}>
              <TableCell align='left'>
                <Status status={status} />
              </TableCell>
              <TableCell align='left'>{row.id}</TableCell>
              <TableCell align='left'>{getTransformedDate(row?.timestamp)}</TableCell>
              <TableCell align='left'>{row.transaction_id}</TableCell>
              <TableCell align='left'>{accountId}</TableCell>
              <TableCell align='left'>{row.subagent_id}</TableCell>
              <TableCell align='left'>{row.remarks}</TableCell>
              <TableCell align='left'>{row.amount}</TableCell>
              <TableCell
                align='right'
                sx={{ cursor: 'pointer', color: 'var(--mui-palette-table-iconDefault)' }}>
                <RoleAccessWrapper
                  accessCondition={ROLES.Transactions.canMatch}
                  currentRole={user?.role}>
                  {isStatusMatch && (
                    <CustomTooltip title='Match'>
                      <CompareIcon
                        onClick={() => {
                          matchTransaction(row.id);
                        }}
                      />
                    </CustomTooltip>
                  )}
                </RoleAccessWrapper>
              </TableCell>
            </TableRow>
          );
        })}
      </TableContentWrapper>
      <TableContentWrapper
        columns={TRANSACTIONS}
        sx={{
          ...customStyles,
          borderLeft: '1px solid var(--mui-palette-background-default)',
          borderRadius: '15px'
        }}>
        {transactions?.items.map((row, id) => {
          const accountId = methodID === 1 ? row.subagent_id : row.account_id;
          const status = row?.status as TStatusType;

          return (
            <TableRow key={id}>
              <TableCell align='left'>
                <Status status={status} />
              </TableCell>
              <TableCell align='left'>{row.id}</TableCell>
              <TableCell align='left'>{getTransformedDate(row?.timestamp)}</TableCell>
              <TableCell align='left'>{row.application_id}</TableCell>
              <TableCell align='left'>{accountId}</TableCell>
              <TableCell align='left'>{row.subagent_id}</TableCell>
              <TableCell align='left'>{row.remarks}</TableCell>
              <TableCell align='left'>{row.income}</TableCell>
              <TableCell align='right' sx={{ cursor: 'pointer' }}>
                {row?.push_message && (
                  <CustomTooltip title='Messages'>
                    <MailIcon
                      onClick={() => {
                        openMessagesModal(row?.push_message || '', row?.timestamp);
                      }}
                    />
                  </CustomTooltip>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableContentWrapper>
    </MethodTableLayout>
  );
};
